import { useCallback, useState } from 'react';

function flattenNestedRows(data) {
  if (!data || !Array.isArray(data)) return [];

  const result = [];
  const stack = [...data];

  while (stack.length > 0) {
    const item = stack.pop();
    if (item) result.push(item);
    if (
      item.children &&
      Array.isArray(item.children) &&
      item.children.length > 0
    ) {
      stack.push(...item.children);
    }
  }

  return result;
}

export function useTableRowSelectionHandler({ getItemId, totalItems }) {
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(
    new Set()
  );
  const [unselectedItemIds, setUnselectedItemIds] = useState<Set<string>>(
    new Set()
  );
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);

  const toggleChildrenSelection = useCallback(
    (item, isSelected) => {
      if (
        !item.children ||
        !Array.isArray(item.children) ||
        item.children.length === 0
      ) {
        return;
      }

      const childrenFlattened = flattenNestedRows(item.children);

      if (isSelectAllActive) {
        setUnselectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) {
            childrenFlattened.forEach((child) => {
              next.delete(getItemId(child));
            });
          } else {
            childrenFlattened.forEach((child) => {
              next.add(getItemId(child));
            });
          }
          return next;
        });
      } else {
        setSelectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) {
            childrenFlattened.forEach((child) => {
              next.add(getItemId(child));
            });
          } else {
            childrenFlattened.forEach((child) => {
              next.delete(getItemId(child));
            });
          }
          return next;
        });
      }
    },
    [getItemId, isSelectAllActive]
  );

  const isItemSelected = useCallback(
    (item) => {
      const itemId = getItemId(item);

      if (isSelectAllActive) {
        // When selectAll is active, item is selected unless it's in unselectedItemIds
        return !unselectedItemIds.has(itemId);
      }

      // Otherwise, check if item's ID is in selectedItemIds
      return selectedItemIds.has(itemId);
    },
    [getItemId, isSelectAllActive, selectedItemIds, unselectedItemIds]
  );

  const handleSelectItem = useCallback(
    (item, isSelected) => {
      const itemId = getItemId(item);

      if (isSelectAllActive) {
        setUnselectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) next.delete(itemId);
          else next.add(itemId);
          return next;
        });
      } else {
        setSelectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) next.add(itemId);
          else next.delete(itemId);
          return next;
        });
      }

      if (
        item.children &&
        Array.isArray(item.children) &&
        item.children.length > 0
      ) {
        toggleChildrenSelection(item, isSelected);
      }
    },
    [getItemId, isSelectAllActive, toggleChildrenSelection]
  );

  const handleSelectAll = useCallback((isSelected) => {
    if (isSelected) {
      setIsSelectAllActive(true);
      setUnselectedItemIds(new Set());
      setSelectedItemIds(new Set());
    } else {
      setIsSelectAllActive(false);
      setSelectedItemIds(new Set());
      setUnselectedItemIds(new Set());
    }
  }, []);

  const handleSelectPage = useCallback(
    (pageItems, isSelected) => {
      const flattenedItems = flattenNestedRows(pageItems);
      const pageItemIds: string[] = flattenedItems.map(getItemId);

      if (isSelectAllActive) {
        setUnselectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) pageItemIds.forEach((id) => next.delete(id));
          else pageItemIds.forEach((id) => next.add(id));
          return next;
        });
      } else {
        setSelectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) pageItemIds.forEach((id) => next.add(id));
          else pageItemIds.forEach((id) => next.delete(id));
          return next;
        });

        if (
          isSelected &&
          selectedItemIds.size + pageItemIds.length >= totalItems
        ) {
          handleSelectAll(true);
        }
      }
    },
    [getItemId, isSelectAllActive, selectedItemIds, totalItems]
  );

  const getSelectedItems = useCallback(
    (pageItems) => {
      const flattenedItems = flattenNestedRows(pageItems);
      return flattenedItems.filter((item) => isItemSelected(item));
    },
    [getItemId, isItemSelected]
  );

  const getSelectionState = useCallback(() => {
    if (isSelectAllActive) {
      return {
        unselectedIds: Array.from(unselectedItemIds),
        totalSelected: totalItems - unselectedItemIds.size,
      };
    }
    return {
      selectedIds: Array.from(selectedItemIds),
      totalSelected: selectedItemIds.size,
    };
  }, [isSelectAllActive, selectedItemIds, totalItems, unselectedItemIds]);

  const resetSelection = useCallback(() => {
    setSelectedItemIds(new Set());
    setUnselectedItemIds(new Set());
    setIsSelectAllActive(false);
  }, []);

  return {
    selectedItemIds,
    unselectedItemIds,
    isSelectAllActive,
    isItemSelected,
    handleSelectItem,
    handleSelectPage,
    handleSelectAll,
    getSelectionState,
    getSelectedItems,
    resetSelection,
    totalSelected: isSelectAllActive
      ? totalItems - unselectedItemIds.size
      : selectedItemIds.size,
  };
}
